import { computeFileMd5OnMainThread } from './compute-file-md5-main-thread';
import type { ComputeFileMd5Progress } from './compute-file-md5-main-thread';
import { resolveDocumentUploadPoolSize } from './document-upload-concurrency';
import type { Md5HashWorkerMessage } from './md5-hash.types';

type PendingMd5HashJob = {
  file: File | Blob;
  chunkSize: number;
  onProgress?: (progress: ComputeFileMd5Progress) => void;
  resolve: (hash: Uint8Array) => void;
  reject: (error: Error) => void;
};

const isBrowserWorkerEnvironment = (): boolean =>
  typeof window !== 'undefined' && typeof Worker !== 'undefined';

const createMd5HashWorker = (): Worker =>
  new Worker(new URL('./md5-hash.worker.ts', import.meta.url), {
    type: 'module',
  });

class Md5HashWorkerPool {
  private readonly workers: Worker[] = [];
  private readonly idleWorkers: Worker[] = [];
  private readonly jobs = new Map<string, PendingMd5HashJob>();
  private readonly workerJobIds = new Map<Worker, string>();
  private readonly jobQueue: Array<{ id: string; worker?: Worker }> = [];

  constructor(poolSize: number) {
    for (let index = 0; index < poolSize; index += 1) {
      const worker = createMd5HashWorker();
      worker.onmessage = event => {
        this.handleWorkerMessage(worker, event.data as Md5HashWorkerMessage);
      };
      worker.onerror = event => {
        this.handleWorkerFailure(
          worker,
          new Error(event.message || 'MD5 worker failed')
        );
      };

      this.workers.push(worker);
      this.idleWorkers.push(worker);
    }
  }

  compute(
    file: File | Blob,
    chunkSize: number,
    onProgress?: (progress: ComputeFileMd5Progress) => void
  ): Promise<Uint8Array> {
    const id = crypto.randomUUID();

    return new Promise((resolve, reject) => {
      this.jobs.set(id, {
        file,
        chunkSize,
        onProgress,
        resolve,
        reject,
      });
      this.jobQueue.push({ id });
      this.dispatchJobs();
    });
  }

  terminate(): void {
    for (const worker of this.workers) {
      worker.terminate();
    }

    this.workers.length = 0;
    this.idleWorkers.length = 0;
    this.workerJobIds.clear();
    this.jobQueue.length = 0;

    for (const job of this.jobs.values()) {
      job.reject(new Error('MD5 worker pool terminated'));
    }

    this.jobs.clear();
  }

  private dispatchJobs(): void {
    while (this.idleWorkers.length > 0 && this.jobQueue.length > 0) {
      const nextJob = this.jobQueue.shift();
      if (!nextJob) return;

      const worker = this.idleWorkers.shift();
      if (!worker) return;

      const job = this.jobs.get(nextJob.id);
      if (!job) {
        this.idleWorkers.push(worker);
        continue;
      }

      this.workerJobIds.set(worker, nextJob.id);
      worker.postMessage({
        type: 'hash',
        id: nextJob.id,
        file: job.file,
        chunkSize: job.chunkSize,
      });
    }
  }

  private finishJob(worker: Worker, jobId: string): PendingMd5HashJob | null {
    this.workerJobIds.delete(worker);
    this.idleWorkers.push(worker);

    const job = this.jobs.get(jobId) ?? null;
    if (job) {
      this.jobs.delete(jobId);
    }

    this.dispatchJobs();

    return job;
  }

  private handleWorkerMessage(
    worker: Worker,
    message: Md5HashWorkerMessage
  ): void {
    if (message.type === 'progress') {
      const job = this.jobs.get(message.id);
      job?.onProgress?.(message.progress);

      return;
    }

    if (message.type === 'result') {
      const job = this.finishJob(worker, message.id);
      job?.resolve(new Uint8Array(message.hash));

      return;
    }

    if (message.type === 'error') {
      const job = this.finishJob(worker, message.id);
      job?.reject(new Error(message.message));
    }
  }

  private handleWorkerFailure(worker: Worker, error: Error): void {
    const jobId = this.workerJobIds.get(worker);
    if (!jobId) return;

    const job = this.finishJob(worker, jobId);
    job?.reject(error);
  }
}

let md5HashWorkerPool: Md5HashWorkerPool | null = null;

const getMd5HashWorkerPool = (): Md5HashWorkerPool | null => {
  if (!isBrowserWorkerEnvironment()) return null;

  if (!md5HashWorkerPool) {
    md5HashWorkerPool = new Md5HashWorkerPool(
      resolveDocumentUploadPoolSize('md5')
    );
  }

  return md5HashWorkerPool;
};

export const computeFileMd5WithWorkerPool = async (
  file: File | Blob,
  chunkSize: number,
  onProgress?: (progress: ComputeFileMd5Progress) => void
): Promise<Uint8Array> => {
  const pool = getMd5HashWorkerPool();

  if (!pool) {
    return computeFileMd5OnMainThread(file, chunkSize, onProgress);
  }

  try {
    return await pool.compute(file, chunkSize, onProgress);
  } catch {
    return computeFileMd5OnMainThread(file, chunkSize, onProgress);
  }
};

export const resetMd5HashWorkerPoolForTests = (): void => {
  md5HashWorkerPool?.terminate();
  md5HashWorkerPool = null;
};
